/**
 * Scheduled Reports Page - Manage report schedules
 */
import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Calendar, 
  Play, 
  Pause, 
  Trash2,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { ScheduledReport } from '../services/reportsApi';
import { schedulingApi } from '../services/schedulingApi';

const ScheduledReports: React.FC = () => {
  const [schedules, setSchedules] = useState<ScheduledReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'paused' | 'completed' | 'failed'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadSchedules();
  }, []);

  useEffect(() => {
    if (searchTerm || statusFilter !== 'all') {
      loadSchedules();
    }
  }, [searchTerm, statusFilter]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const params: any = {
        search: searchTerm || undefined,
      };
      
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const data = await schedulingApi.listSchedules(params);
      setSchedules(Array.isArray(data) ? data : data.results || []);
    } catch (error) {
      console.error('Error loading schedules:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (schedule: ScheduledReport) => {
    try {
      await schedulingApi.activateSchedule(schedule.id);
      loadSchedules();
    } catch (error) {
      console.error('Error activating schedule:', error);
    }
  };

  const handleDeactivate = async (schedule: ScheduledReport) => {
    try {
      await schedulingApi.deactivateSchedule(schedule.id);
      loadSchedules();
    } catch (error) {
      console.error('Error deactivating schedule:', error);
    }
  };

  const handleRunNow = async (schedule: ScheduledReport) => {
    try {
      await schedulingApi.runScheduleNow(schedule.id);
      alert('Relatório executado com sucesso!');
      loadSchedules();
    } catch (error) {
      console.error('Error running schedule:', error);
      alert('Erro ao executar relatório. Tente novamente.');
    }
  };

  const handleDelete = async (schedule: ScheduledReport) => {
    if (window.confirm(`Deseja realmente excluir o agendamento "${schedule.name}"?`)) {
      try {
        await schedulingApi.deleteSchedule(schedule.id);
        loadSchedules();
      } catch (error) {
        console.error('Error deleting schedule:', error);
      }
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'paused':
        return <Pause className="w-5 h-5 text-yellow-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      active: 'Ativo',
      paused: 'Pausado',
      completed: 'Concluído',
      failed: 'Falhou',
    };
    return labels[status] || status;
  };

  const getScheduleLabel = (schedule: string) => {
    const labels: Record<string, string> = {
      daily: 'Diariamente',
      weekly: 'Semanalmente',
      monthly: 'Mensalmente',
      quarterly: 'Trimestralmente',
      yearly: 'Anualmente',
    };
    return labels[schedule] || schedule;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '3px solid var(--glass-border)',
          borderTopColor: 'var(--color-primary-600)',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-6)' }}>
      {/* Header */}
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', color: 'var(--text-primary)' }}>Relatórios Agendados</h1>
        <p style={{ marginTop: 'var(--space-2)', color: 'var(--text-secondary)' }}>
          Gerencie seus relatórios agendados e automações
        </p>
      </div>

      {/* Toolbar */}
      <div style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-4)', marginBottom: 'var(--space-6)', boxShadow: 'var(--shadow-glass)' }}>
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar agendamentos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Status Filters */}
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setStatusFilter('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'active'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Ativos
            </button>
            <button
              onClick={() => setStatusFilter('paused')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'paused'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pausados
            </button>
          </div>

          {/* Create Button */}
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5" />
            Agendar Relatório
          </button>
        </div>
      </div>

      {/* Schedules List */}
      {schedules.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-600 mb-2">
            Nenhum agendamento encontrado
          </h3>
          <p className="text-gray-500 mb-4">
            {searchTerm
              ? 'Tente buscar com outro termo'
              : 'Agende seu primeiro relatório'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              Agendar Relatório
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {schedules.map((schedule) => (
            <div
              key={schedule.id}
              style={{ background: 'var(--glass-bg-strong)', backdropFilter: 'var(--glass-blur)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--glass-border)', padding: 'var(--space-5)', boxShadow: 'var(--shadow-glass)' }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {schedule.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusIcon(schedule.status)}
                      <span className="text-sm text-gray-600">
                        {getStatusLabel(schedule.status)} • {getScheduleLabel(schedule.schedule)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {schedule.active ? (
                    <button
                      onClick={() => handleDeactivate(schedule)}
                      className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                      title="Pausar"
                    >
                      <Pause className="w-5 h-5 text-yellow-600" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleActivate(schedule)}
                      className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                      title="Ativar"
                    >
                      <Play className="w-5 h-5 text-green-600" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(schedule)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                    title="Excluir"
                  >
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              </div>

              {/* Schedule Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Horário: {schedule.run_time}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Próxima execução:{' '}
                    {schedule.next_run
                      ? new Date(schedule.next_run).toLocaleString('pt-BR')
                      : 'Não agendado'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    Última execução:{' '}
                    {schedule.last_run
                      ? new Date(schedule.last_run).toLocaleString('pt-BR')
                      : 'Nunca'}
                  </span>
                </div>
              </div>

              {/* Template Info */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Template:</span>{' '}
                  {schedule.template_name}
                </div>
                <button
                  onClick={() => handleRunNow(schedule)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors font-medium text-sm"
                >
                  <Play className="w-4 h-4" />
                  Executar Agora
                </button>
              </div>

              {/* Recipients */}
              {schedule.recipients && schedule.recipients.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-600 font-medium">Destinatários:</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {schedule.recipients.map((email, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 rounded-md text-sm"
                      >
                        {email}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal - Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Agendar Relatório
            </h2>
            <p className="text-gray-600 mb-6">
              A funcionalidade de agendamento será implementada na próxima etapa.
            </p>
            <button
              onClick={() => setShowCreateModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledReports;
